import * as React from 'react';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { UserEntityDetails } from '../../axiosApi/models';
import { Alert, Box, Card, Paper, Skeleton, Stack, Typography } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { getUserEntity } from 'common/UserEntityProvider';
import { handleAPIError } from '../../common/errorHandler';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import Title from 'components/common/Title';
import { ReportsFilterUtilization, ReportsFilterUtilizationProps } from './ReportsFilterUtilization';
import { Chart, useChart } from '../chart';
import { ACCESSIBLE_COLORS } from '../../common/constants';
import { CompareArrows } from '@mui/icons-material';

interface AllocationData {
    date: string;
    allocationPercentage: number;
}

interface ProjectData {
    idProject: number;
    projectName: string;
    allocations: AllocationData[];
}

interface EmployeeAllocationData {
    idEmployee: number;
    employeeName: string;
    projects: ProjectData[];
}

interface TimesheetTask {
    idTask: number;
    taskDescription: string;
    timesheetDescription: string;
    time: number;
    idEmployee: number;
    employeeName: string;
    date: Date;
}

interface TimesheetJob {
    idJob: number;
    jobDescription: string;
    tasks: TimesheetTask[];
    billableHours: number;
    totalHours: number;
    nonBillableHours: number;
}

interface TimesheetProject {
    idProject: number;
    projectName: string;
    jobs: TimesheetJob[];
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
}

interface TimesheetClient {
    idClient: number;
    clientName: string;
    projects: TimesheetProject[];
    totalHours: number;
    billableHours: number;
    nonBillableHours: number;
}

type AllocationVsTimesheetReportComponentProps = {
    client?: boolean;
}

export const AllocationVsTimesheetReportComponent = (props: AllocationVsTimesheetReportComponentProps) => {
    const { client } = props;
    const api: Api = useApi();
    const toast = useToastMessageQueue();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [errors, setErrors] = React.useState({});
    const [comparisonData, setComparisonData] = React.useState<any[]>([]);
    const [selectedEmployees, setSelectedEmployees] = React.useState<number[]>([]);
    const userEntity: UserEntityDetails = getUserEntity();

    const fetchComparisonData = async (filterData: ReportsFilterUtilizationProps) => {
        setLoading(true);
        try {
            let employeeIds = filterData.employees
                ? filterData.employees.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
                : [];

            // If no employees selected, fetch all active employees
            if (employeeIds.length === 0) {
                if (!filterData.from || !filterData.to) {
                    setComparisonData([]);
                    setSelectedEmployees([]);
                    setLoading(false);
                    return;
                }
                
                const employeeResponse = await api.employeeApi.apiVversionListEmployeesGet("1", false, {});
                //@ts-expect-error TODO
                const employeesList: any = employeeResponse.data?.data?.data?.queryResult;
                if (Array.isArray(employeesList) && employeesList.length > 0) {
                    employeeIds = employeesList.map((emp) => Number(emp.id));
                } else {
                    setComparisonData([]);
                    setSelectedEmployees([]);
                    setLoading(false);
                    return;
                }
            }

            if (!filterData.from || !filterData.to) {
                setComparisonData([]);
                setSelectedEmployees([]);
                setLoading(false);
                return;
            }

            // Batch processing to avoid API overload - split into chunks of 10 employees
            const BATCH_SIZE = 10;
            const batches: number[][] = [];
            for (let i = 0; i < employeeIds.length; i += BATCH_SIZE) {
                batches.push(employeeIds.slice(i, i + BATCH_SIZE));
            }

            console.log(`Fetching allocation data for ${employeeIds.length} employees in ${batches.length} batches...`);

            // Fetch allocation data (planned) in batches
            const allocationPromises = batches.map(async (batchIds) => {
                try {
                    const response = await api.reportsApi.apiVversionEmployeeAllocationReportGetRangeGet(
                        "1",
                        filterData.from.toISOString().split('T')[0],
                        filterData.to.toISOString().split('T')[0],
                        batchIds,
                        {}
                    );
                    return (response.data as any)?.data || [];
                } catch (error) {
                    console.error(`Error fetching allocation data for batch:`, batchIds, error);
                    return [];
                }
            });

            const allocationBatches = await Promise.all(allocationPromises);
            const allocationData: EmployeeAllocationData[] = (allocationBatches.flat() as any);

            console.log(`Successfully fetched allocation data for ${allocationData.length} employees out of ${employeeIds.length} requested`);
            console.log(`Batch results:`, allocationBatches.map((batch, i) => `Batch ${i + 1}: ${batch.length} employees`));

            // Fetch timesheet data (actual)
            const timesheetResponse = await api.reportsApi.apiVversionTimeSheetDetailedReportAllGet(
                "1",
                filterData.from,
                filterData.to,
                undefined, // projects
                undefined, // clients
                filterData.employees, // employees as string
                undefined, // jobTypes
                {}
            );

            // Process and merge the data
            //@ts-expect-error TODO
            const timesheetData: TimesheetClient[] = timesheetResponse.data?.data || [];

            console.log('Allocation data employees:', allocationData.length);
            console.log('Allocation data sample:', allocationData.slice(0, 3));
            console.log('Timesheet data:', timesheetData.length, 'clients');
            console.log('Employee IDs to process:', employeeIds.length, employeeIds);

            const merged = mergeAllocationAndTimesheet(allocationData, timesheetData, employeeIds);
            
            console.log('Merged data employees:', merged.length);
            console.log('Merged data sample:', merged.slice(0, 3));
            
            setComparisonData(merged);
            setSelectedEmployees(employeeIds);
            setLoading(false);
        } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            setLoading(false);
        }
    };

    // Merge allocation (planned) and timesheet (actual) data
    const mergeAllocationAndTimesheet = (
        allocationData: EmployeeAllocationData[],
        timesheetData: TimesheetClient[],
        employeeIds: number[]
    ) => {
        const result: any[] = [];

        // Create a map of allocation data by employee ID for quick lookup
        const allocationMap = new Map<number, EmployeeAllocationData>();
        allocationData.forEach(emp => {
            allocationMap.set(emp.idEmployee, emp);
        });

        // Create a map of timesheet hours by employee, project, and date
        const timesheetMap = new Map<string, number>(); // key: "employeeId-projectId-date", value: hours
        const timesheetEmployeeMap = new Map<number, Set<number>>(); // employee -> set of project IDs
        
        timesheetData.forEach(client => {
            client.projects.forEach(project => {
                project.jobs.forEach(job => {
                    job.tasks.forEach(task => {
                        const dateStr = new Date(task.date).toISOString().split('T')[0];
                        const key = `${task.idEmployee}-${project.idProject}-${dateStr}`;
                        const currentHours = timesheetMap.get(key) || 0;
                        timesheetMap.set(key, currentHours + task.time);
                        
                        // Track which employees have timesheet data
                        if (!timesheetEmployeeMap.has(task.idEmployee)) {
                            timesheetEmployeeMap.set(task.idEmployee, new Set());
                        }
                        timesheetEmployeeMap.get(task.idEmployee)!.add(project.idProject);
                    });
                });
            });
        });

        console.log('Timesheet map entries:', timesheetMap.size);
        console.log('Employees with timesheet data:', timesheetEmployeeMap.size);
        console.log('Employees with allocation data:', allocationMap.size);

        // Process ALL requested employees
        employeeIds.forEach(employeeId => {
            const employee = allocationMap.get(employeeId);
            
            if (employee) {
                // Employee has allocation data
                const employeeResult = {
                    idEmployee: employee.idEmployee,
                    employeeName: employee.employeeName,
                    projects: [] as any[]
                };

                employee.projects.forEach(project => {
                    const projectResult = {
                        idProject: project.idProject,
                        projectName: project.projectName,
                        comparisons: [] as any[]
                    };

                    project.allocations.forEach(allocation => {
                        const key = `${employee.idEmployee}-${project.idProject}-${allocation.date}`;
                        const actualHours = timesheetMap.get(key) || 0;
                        
                        // Convert allocation percentage to hours (assuming 8-hour workday)
                        const plannedHours = (allocation.allocationPercentage / 100) * 8;
                        
                        // Calculate variance (positive = over-logged, negative = under-logged)
                        const variance = actualHours - plannedHours;
                        const variancePercentage = plannedHours > 0 ? (variance / plannedHours) * 100 : 0;

                        projectResult.comparisons.push({
                            date: allocation.date,
                            plannedHours,
                            plannedPercentage: allocation.allocationPercentage,
                            actualHours,
                            variance,
                            variancePercentage
                        });
                    });

                    if (projectResult.comparisons.length > 0) {
                        employeeResult.projects.push(projectResult);
                    }
                });

                if (employeeResult.projects.length > 0) {
                    result.push(employeeResult);
                } else {
                    console.log(`Employee ${employee.employeeName} (${employee.idEmployee}) has allocation but no comparison data`);
                }
            } else {
                // Employee has NO allocation data - check if they have timesheet data
                console.log(`Employee ID ${employeeId} has no allocation data from API`);
            }
        });

        console.log(`Merge complete: ${employeeIds.length} requested -> ${allocationData.length} with allocations -> ${result.length} with comparison data`);

        return result;
    };

    // Transform comparison data into heatmap format
    const getVarianceHeatmapData = () => {
        if (comparisonData.length === 0) return null;

        // Collect all unique dates
        const allDates = new Set<string>();
        comparisonData.forEach(employee => {
            employee.projects.forEach((project: any) => {
                project.comparisons.forEach((comp: any) => {
                    allDates.add(comp.date);
                });
            });
        });

        const sortedDates = Array.from(allDates).sort();
        if (sortedDates.length === 0) return null;

        // Calculate date range for adaptive grouping
        const firstDate = new Date(sortedDates[0]);
        const lastDate = new Date(sortedDates[sortedDates.length - 1]);
        const monthsDiff = (lastDate.getFullYear() - firstDate.getFullYear()) * 12 + (lastDate.getMonth() - firstDate.getMonth());
        const yearsDiff = monthsDiff / 12;

        // Determine grouping strategy
        let groupingType: 'daily' | 'weekly' | 'biweekly' | 'monthly';
        if (monthsDiff <= 3) {
            groupingType = 'daily';
        } else if (monthsDiff <= 12) {
            groupingType = 'weekly';
        } else if (yearsDiff <= 5) {
            groupingType = 'biweekly';
        } else {
            groupingType = 'monthly';
        }

        const getWeekKey = (dateStr: string) => {
            const date = new Date(dateStr);
            const day = date.getDay();
            const diff = date.getDate() - day;
            const monday = new Date(date.setDate(diff));
            return monday.toISOString().split('T')[0];
        };

        const getBiweekKey = (dateStr: string) => {
            const date = new Date(dateStr);
            const day = date.getDay();
            const diff = date.getDate() - day;
            const monday = new Date(date.setDate(diff));
            const weekNum = Math.floor((monday.getTime() - new Date(monday.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000));
            const biweekNum = Math.floor(weekNum / 2);
            const biweekStart = new Date(monday.getFullYear(), 0, 1 + (biweekNum * 14));
            return biweekStart.toISOString().split('T')[0];
        };

        const getMonthKey = (dateStr: string) => {
            const date = new Date(dateStr);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
        };

        if (groupingType === 'daily') {
            // Daily variance data
            const series = comparisonData.map(employee => {
                const varianceMap = new Map<string, { variance: number, count: number }>();

                employee.projects.forEach((project: any) => {
                    project.comparisons.forEach((comp: any) => {
                        const existing = varianceMap.get(comp.date) || { variance: 0, count: 0 };
                        varianceMap.set(comp.date, {
                            variance: existing.variance + comp.variancePercentage,
                            count: existing.count + 1
                        });
                    });
                });

                const data = sortedDates.map(date => {
                    const value = varianceMap.get(date);
                    if (!value) return 0;
                    return Math.round((value.variance / value.count) * 10) / 10;
                });

                return {
                    name: employee.employeeName,
                    data: data
                };
            });

            return {
                dates: sortedDates.map(d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                series: series
            };
        } else {
            // Grouped variance data
            const periodMap = new Map<string, string[]>();
            const getKeyFunc = groupingType === 'weekly' ? getWeekKey : groupingType === 'biweekly' ? getBiweekKey : getMonthKey;

            sortedDates.forEach(date => {
                const periodKey = getKeyFunc(date);
                if (!periodMap.has(periodKey)) {
                    periodMap.set(periodKey, []);
                }
                periodMap.get(periodKey)!.push(date);
            });

            const periods = Array.from(periodMap.keys()).sort();

            const series = comparisonData.map(employee => {
                const varianceMap = new Map<string, { variance: number, count: number }>();

                employee.projects.forEach((project: any) => {
                    project.comparisons.forEach((comp: any) => {
                        const existing = varianceMap.get(comp.date) || { variance: 0, count: 0 };
                        varianceMap.set(comp.date, {
                            variance: existing.variance + comp.variancePercentage,
                            count: existing.count + 1
                        });
                    });
                });

                const periodData = periods.map(periodKey => {
                    const datesInPeriod = periodMap.get(periodKey)!;
                    let totalVariance = 0;
                    let daysWithData = 0;

                    datesInPeriod.forEach(date => {
                        const value = varianceMap.get(date);
                        if (value && value.count > 0) {
                            totalVariance += (value.variance / value.count);
                            daysWithData++;
                        }
                    });

                    if (daysWithData === 0) return 0;
                    return Math.round((totalVariance / daysWithData) * 10) / 10;
                });

                return {
                    name: employee.employeeName,
                    data: periodData
                };
            });

            // Format period labels
            const formattedDates = periods.map(p => {
                const periodStart = new Date(p);
                if (groupingType === 'monthly') {
                    return periodStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                } else if (groupingType === 'biweekly') {
                    const periodEnd = new Date(p);
                    periodEnd.setDate(periodEnd.getDate() + 13);
                    return `${periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                } else { // weekly
                    const periodEnd = new Date(p);
                    periodEnd.setDate(periodEnd.getDate() + 6);
                    return `${periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                }
            });

            return {
                dates: formattedDates,
                series: series
            };
        }
    };

    const varianceHeatmapData = React.useMemo(() => getVarianceHeatmapData(), [comparisonData]);

    // Custom color scale for variance (red = under-logged, green = over-logged)
    const varianceHeatmapOptions = useChart({
        chart: {
            type: 'heatmap',
            toolbar: {
                show: true,
                tools: {
                    download: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        colors: ['#008FFB'],
        xaxis: {
            categories: varianceHeatmapData?.dates || [],
            labels: {
                rotate: -45,
                rotateAlways: true,
                style: {
                    fontSize: '10px'
                }
            }
        },
        plotOptions: {
            heatmap: {
                colorScale: {
                    ranges: [
                        {
                            from: -100,
                            to: -50,
                            color: '#D32F2F', // Dark red - severely under-logged
                            name: intl.get('reports.variance.severelyUnder')
                        },
                        {
                            from: -49,
                            to: -20,
                            color: '#FF6B6B', // Light red - under-logged
                            name: intl.get('reports.variance.under')
                        },
                        {
                            from: -19,
                            to: -5,
                            color: '#FFE066', // Yellow - slightly under
                            name: intl.get('reports.variance.slightlyUnder')
                        },
                        {
                            from: -4,
                            to: 4,
                            color: '#95E1D3', // Light cyan - on target
                            name: intl.get('reports.variance.onTarget')
                        },
                        {
                            from: 5,
                            to: 19,
                            color: '#66BB6A', // Light green - slightly over
                            name: intl.get('reports.variance.slightlyOver')
                        },
                        {
                            from: 20,
                            to: 49,
                            color: '#4CAF50', // Green - over-logged
                            name: intl.get('reports.variance.over')
                        },
                        {
                            from: 50,
                            to: 200,
                            color: '#2E7D32', // Dark green - severely over-logged
                            name: intl.get('reports.variance.severelyOver')
                        }
                    ]
                }
            }
        },
        tooltip: {
            y: {
                formatter: function (value) {
                    if (value === 0) return intl.get('reports.variance.noData');
                    const sign = value > 0 ? '+' : '';
                    return `${sign}${value.toFixed(1)}%`;
                }
            }
        }
    });

    return (
        <Card sx={{ m: 2 }}>
            <Box sx={{ p: 2 }}>
                <Title>{intl.get('reports.allocationVsTimesheet.title')}</Title>
                <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                    <CompareArrows /> {intl.get('reports.allocationVsTimesheet.title')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Stack spacing={2}>
                        <ReportsFilterUtilization
                            handleFilterChange={fetchComparisonData}
                            errors={errors}
                            setErrors={setErrors}
                        />
                        
                        {/* Info message showing data availability */}
                        {!loading && selectedEmployees.length > 0 && comparisonData.length !== selectedEmployees.length && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {intl.get('reports.allocationVsTimesheet.dataInfo', {
                                        showing: comparisonData.length,
                                        total: selectedEmployees.length,
                                        missing: selectedEmployees.length - comparisonData.length
                                    })}
                                </Typography>
                            </Alert>
                        )}

                        {!loading && comparisonData.length === 0 && selectedEmployees.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {intl.get('reports.allocationVsTimesheet.noData')}
                                </Typography>
                            </Alert>
                        )}

                        <PermissionsGate
                            identifier="ui.reports.allocation-timesheet.gate"
                            viewRoles={[]}
                            editRoles={[]}
                        >
                            {loading && (
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ mb: 4 }}>
                                        <Skeleton variant="text" width="30%" height={40} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
                                        <Skeleton variant="rectangular" width="100%" height={400} />
                                    </Box>
                                </Box>
                            )}

                            {/* Variance Heatmap */}
                            {!loading && comparisonData.length > 0 && varianceHeatmapData && (
                                <Box>
                                    <Typography variant="h5" sx={{ mb: 2 }}>
                                        {intl.get('reports.variance.heatmap.title')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {intl.get('reports.variance.heatmap.description')}
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Chart
                                            type="heatmap"
                                            series={varianceHeatmapData.series}
                                            options={varianceHeatmapOptions}
                                            height={Math.max(400, comparisonData.length * 40)}
                                        />
                                    </Paper>
                                </Box>
                            )}

                            {!loading && comparisonData.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        {intl.get('reports.variance.selectEmployees')}
                                    </Typography>
                                </Box>
                            )}
                        </PermissionsGate>
                    </Stack>
                </Box>
            </Box>
        </Card>
    );
}

export default AllocationVsTimesheetReportComponent;
